import React, { useState, useEffect } from 'react';

import AlgorithmSelector from '../components/AlgorithmSelector';
import ParameterForm from '../components/ParameterForm';
import TrainingLogs from '../components/TrainingLogs';
import ResultsDisplay from '../components/ResultsDisplay';
import TrainingChart from '../components/TrainingChart';
import ResultPreview from '../components/ResultPreview';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

// 卡片组件
const Card = ({ title, children, onClick, disabled }) => {
  const handleClick = () => {
    if (!disabled && typeof onClick === 'function') {
      onClick();
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-2xl shadow-md p-6 border border-gray-200 transform transition-transform hover:scale-105 hover:shadow-xl ${
        disabled ? 'cursor-default' : 'cursor-pointer'
      }`}
    >
      <h2 className="text-lg font-semibold text-gray-800 mb-4">{title}</h2>
      {children}
    </div>
  );
};

const SPTComparison = () => {
  const [selectedAlgorithm, setSelectedAlgorithm] = useState(null);
  const [view, setView] = useState('dashboard');
  const [trainingLogs, setTrainingLogs] = useState('');
  const [resultVideo, setResultVideo] = useState('/placeholders/preview.mp4');
  const [currentJobId, setCurrentJobId] = useState(null);

  const navigate = useNavigate();
  const showDashboard = () => setView('dashboard');

  // 🔥 关键函数：把参数保存成「job」，让后端写到
  // s3://sptcloud/jobs/{user_id}/{job_id}/{algorithm}/sim_params.json, training_params.json
  const saveJobToS3 = async ({ simParams, trainingParams }) => {
    try {
      if (!selectedAlgorithm) {
        alert('请先选择算法');
        return;
      }

      // 1. 拿当前登录用户（看你项目里怎么存的，可以按需改）
      let userData = null;
      try {
        userData =
          JSON.parse(localStorage.getItem('user')) ||
          JSON.parse(localStorage.getItem('authUser')) ||
          null;
      } catch (e) {
        userData = null;
      }

      const username =
        userData?.username ||
        userData?.email ||
        'anonymous';

      // 2. 组织 payload（不再传 instance_id，job_id 由后端生成）
      const payload = {
        username,
        algorithm: selectedAlgorithm,
        sim_params: simParams || {},
        training_params: trainingParams || {},
      };

      console.log('📤 创建 job 并保存参数到 S3，payload =', payload);

      // 3. 调用后端（建议你在 Django 配的 URL 是 /api/params/save/）
      const res = await axios.post('/api/params/save/', payload, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      });

      console.log('✅ 后端返回：', res.data);

      if (res.data?.job_id) {
        setCurrentJobId(res.data.job_id);
      }

      alert(
        `参数已保存到 S3，并创建 job！\n` +
          `job_id: ${res.data.job_id || '未知'}\n` +
          `S3 prefix: ${res.data.s3_prefix || ''}`
      );
    } catch (err) {
      console.error('❌ 保存 job 失败：', err);
      alert('保存参数 / 创建 job 失败，请看浏览器控制台日志');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      {/* 顶部 View History 链接 */}
      <div className="mt-8 text-right">
        <Link
          to="/history"
          className="text-sm text-blue-500 underline hover:text-blue-700"
        >
          🕓 View History
        </Link>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* 标题区 */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-800">
            🧪 SPT Algorithm Dashboard
          </h1>
          {selectedAlgorithm && (
            <p className="text-gray-600 mt-2">
              Current algorithm: {selectedAlgorithm}
            </p>
          )}
          {currentJobId && (
            <p className="text-gray-500 text-sm mt-1">
              Latest job_id: {currentJobId}
            </p>
          )}
        </div>

        {/* === Dashboard View === */}
        {view === 'dashboard' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* 算法选择 */}
            <Card title="🧠 Algorithm Selector">
              <AlgorithmSelector
                onChange={(algo) => {
                  setSelectedAlgorithm(algo);
                  // ⭐ 只新增这一行：把算法名写到 localStorage，给 /simulate 用
                  localStorage.setItem('selectedAlgorithm', algo);
                }}
              />
            </Card>

            {/* 参数配置 */}
            <Card
              title="📋 Parameter Configuration"
              disabled={!selectedAlgorithm}
              onClick={() => {
                console.log('Selected algorithm:', selectedAlgorithm);
                if (
                  selectedAlgorithm &&
                  selectedAlgorithm.includes('SPTNet')
                ) {
                  console.log('Navigating to /simulate');
                  navigate('/simulate');
                } else if (
                  selectedAlgorithm &&
                  selectedAlgorithm.includes('TrackMate')
                ) {
                  navigate('/trackmate');
                } else {
                  setView('params');
                }
              }}
            >
              <ParameterForm
                algorithm={selectedAlgorithm}
                onLogUpdate={setTrainingLogs}
                onVideoUpdate={setResultVideo}
                // 保持你原来的 onSaveJob 逻辑不动
                onSaveJob={saveJobToS3}
              />
            </Card>

            {/* 训练日志 */}
            <Card
              title="🧾 Training Logs"
              disabled={!selectedAlgorithm}
              onClick={() => setView('logs')}
            >
              <TrainingLogs logs={trainingLogs} />
            </Card>

            {/* 结果预览 */}
            <Card
              title="🎞️ Result Preview"
              disabled={!selectedAlgorithm}
              onClick={() => setView('preview')}
            >
              <video
                src={resultVideo}
                controls
                muted
                autoPlay
                loop
                className="w-full h-40 object-cover rounded-lg shadow"
              />
            </Card>
          </div>
        )}

        {/* === Parameter View === */}
        {view === 'params' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">📋 Parameter Configuration</h2>
              <button
                onClick={showDashboard}
                className="text-sm bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
              >
                ← Back to Dashboard
              </button>
            </div>
            <ParameterForm
              algorithm={selectedAlgorithm}
              onLogUpdate={setTrainingLogs}
              onVideoUpdate={setResultVideo}
              onSaveJob={saveJobToS3}
            />
          </div>
        )}

        {/* === Logs View === */}
        {view === 'logs' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">🧾 Training Logs</h2>
              <button
                onClick={showDashboard}
                className="text-sm bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
              >
                ← Back to Dashboard
              </button>
            </div>

            <TrainingLogs logs={trainingLogs} />
            <TrainingChart />
          </div>
        )}

        {/* === Preview View === */}
        {view === 'preview' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">🎞️ Result Preview</h2>
              <button
                onClick={showDashboard}
                className="text-sm bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
              >
                ← Back to Dashboard
              </button>
            </div>

            <video
              src={resultVideo}
              controls
              autoPlay
              muted
              loop
              className="w-full max-h-[400px] rounded-xl shadow"
            />
          </div>
        )}

        {/* 底部导航链接 */}
        <div className="mt-8 text-right">
          <Link
            to="/history"
            className="text-sm text-blue-500 underline hover:text-blue-700"
          >
            🕓 View Training History →
          </Link>
        </div>

        <div className="mt-8 text-right">
          <Link
            to="/explore"
            className="text-blue-500 underline text-sm"
          >
            ➡️ Compare Algorithm Results
          </Link>
        </div>

        <div className="mt-8 text-right">
          <Link
            to="/upload"
            className="text-sm text-blue-500 underline hover:text-blue-700"
          >
            📂 Upload Your Experimental Results and Trajectories →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SPTComparison;
